import { c as _c } from "react/compiler-runtime";
/**
 * File: use-rundschau-livestream.ts
 *
 * This file implements a custom React hook for managing the BR24 Rundschau livestream state.
 *
 * Overview:
 * The Rundschau livestream feature shows viewers the current status of the BR24 TV broadcast:
 * - Before a broadcast starts (PRE_LIVE): Shows a countdown
 * - During a broadcast (NOW_LIVE): Shows the live stream
 * - After a broadcast (PAST): Shows recordings of previous broadcasts
 *
 * The hook manages the complex state transitions between these states, handling:
 * - Automatic polling for updates with optimized intervals
 * - Scheduled transitions between states
 * - Fallback to previous episodes when needed
 * - Configurable pre-live window duration via parameter
 *
 * This implementation is used by the BoardSectionItemBR24Rundschau component
 * to display the appropriate UI for each state.
 */
import { useQuery } from '@apollo/client/react';
import { useRerender } from '@react-hookz/web/useRerender/index.js';
import { useTimeoutEffect } from '@react-hookz/web/useTimeoutEffect/index.js';
import { differenceInMilliseconds, differenceInMinutes, addMilliseconds, subMilliseconds } from 'date-fns';
import { useEffect } from 'react';
import { useCurrentDate } from '#cutils/context/TimeContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { RundschauUpdateQuery } from './__generated__/use-rundschau-livestream.generated';
import { RundschauUpdateDocument } from './__generated__/use-rundschau-livestream.generated';

/**
 * Represents a single episode from the Rundschau broadcast show.
 * This type extracts a non-null episode node from the GraphQL query result.
 */
type Episode = NonNullable<NonNullable<NonNullable<NonNullable<RundschauUpdateQuery['rundschau']>['episodes']>['nodes']>[number]>;

/**
 * Time constants for polling intervals
 */
const ONE_MINUTE_IN_MS = 60_000;
const FIVE_MINUTES_IN_MS = 5 * ONE_MINUTE_IN_MS;
const THIRTY_MINUTES_IN_MS = 30 * ONE_MINUTE_IN_MS;
const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;

/**
 * Represents the current status of the Rundschau livestream.
 *
 * The livestream can be in one of three states:
 * 1. PRE_LIVE: The livestream will start soon (within the pre-live window)
 * 2. NOW_LIVE: The livestream is currently active
 * 3. PAST: The livestream has ended, and recordings may be available
 *
 * Each state contains metadata about the stream including start/end times,
 * modification date, and content-specific data like URLs or video files.
 */
type LiveStreamStatus = {
  status: 'PRE_LIVE' | 'NOW_LIVE';
  startDate: Date | null;
  endDate: Date | null;
  modifiedAt: Date | null;
  liveStreamUrl: string;
  defaultTeaserImage: Episode['defaultTeaserImage'];
  nextStateTimeout: number | undefined;
} | {
  status: 'PAST';
  startDate: Date | null;
  endDate: Date | null;
  modifiedAt: Date | null;
  onDemandVideos: string[];
  defaultTeaserImage: Episode['defaultTeaserImage'];
  nextStateTimeout: number | undefined;
};

/**
 * Determines the current livestream status based on the current time and episode data.
 *
 * This function evaluates the current state of the Rundschau livestream by comparing
 * the current time against the start and end times of the episode. It handles three
 * distinct states:
 *
 * 1. PRE_LIVE: Current time is within the pre-live window before the episode starts
 * 2. NOW_LIVE: Current time is between the start and end of the episode
 * 3. PAST: Current time is after the end of the episode
 *
 * If the episode is in PAST state but has no video files, it will fall back to using
 * a previous episode's recordings while scheduling a transition back to the main episode
 * when it's about to start.
 *
 * @param currentDate The current date/time (adjusted for any time offset)
 * @param episode The primary episode to evaluate
 * @param fallbackEpisode A previous episode to use as fallback if needed
 * @param preLive The duration in milliseconds before the start time when the PRE_LIVE state should begin
 * @returns A LiveStreamStatus object representing the current state, or null if insufficient data
 */
function getLivestreamStatus(currentDate: Date, episode: Episode | null, fallbackEpisode: Episode | null, preLive: number): LiveStreamStatus | null {
  if (!episode?.initialScreening) {
    return null;
  }
  const start = episode.initialScreening.start ? new Date(episode.initialScreening.start) : null;
  const end = episode.initialScreening.end ? new Date(episode.initialScreening.end) : null;
  const modifiedAt = episode.modifiedAt ? new Date(episode.modifiedAt) : null;
  const startBuffer = start ? new Date(start.getTime() - preLive) : null;

  // if we are in the prelive state
  if (start !== null && startBuffer !== null && currentDate >= startBuffer && currentDate < start) {
    const liveStreamUrl = episode.broadcasts?.nodes?.[0]?.broadcastedOn?.nodes?.[0]?.streamingUrls?.nodes?.[0]?.publicLocation || null;
    if (!liveStreamUrl) {
      return null;
    }
    return {
      status: 'PRE_LIVE',
      startDate: start,
      endDate: end,
      liveStreamUrl,
      modifiedAt,
      defaultTeaserImage: episode.defaultTeaserImage,
      // MediaLivestreamStatus handles this transition
      nextStateTimeout: undefined
    };
  }

  // if we are in the live state
  if (start !== null && end !== null && currentDate >= start && currentDate <= end) {
    const liveStreamUrl = episode.broadcasts?.nodes?.[0]?.broadcastedOn?.nodes?.[0]?.streamingUrls?.nodes?.[0]?.publicLocation || null;
    if (!liveStreamUrl) {
      return null;
    }

    // Calculate time until the livestream ends
    const timeUntilStreamEnds = end ? differenceInMilliseconds(end, currentDate) : undefined;
    return {
      status: 'NOW_LIVE',
      liveStreamUrl,
      startDate: start,
      endDate: end,
      modifiedAt,
      defaultTeaserImage: episode.defaultTeaserImage,
      nextStateTimeout: timeUntilStreamEnds
    };
  }
  const episodeHasVideoFiles = (episode.videoFiles?.nodes?.length ?? 0) > 0;

  // the status is past and video files are available for the current episode
  if (episodeHasVideoFiles) {
    const onDemandVideos = episode?.videoFiles?.nodes?.filter(notNullOrUndefined).map(({
      publicLocation
    }) => publicLocation).filter(notNullOrUndefined) ?? [];
    return {
      status: 'PAST',
      onDemandVideos,
      startDate: start,
      endDate: end,
      modifiedAt,
      defaultTeaserImage: episode.defaultTeaserImage,
      nextStateTimeout: undefined
    };
  } else {
    if (!fallbackEpisode?.initialScreening) {
      return null;
    }
    const fallbackStart = fallbackEpisode.initialScreening.start ? new Date(fallbackEpisode.initialScreening.start) : null;
    const fallbackEnd = fallbackEpisode.initialScreening.end ? new Date(fallbackEpisode.initialScreening.end) : null;
    const fallbackModifiedAt = fallbackEpisode.modifiedAt ? new Date(fallbackEpisode.modifiedAt) : null;
    const onDemandVideos = fallbackEpisode?.videoFiles?.nodes?.filter(notNullOrUndefined).map(({
      publicLocation
    }) => publicLocation).filter(notNullOrUndefined) ?? [];
    const timeout = start ? differenceInMilliseconds(subMilliseconds(start, preLive), currentDate) : undefined;
    return {
      status: 'PAST',
      onDemandVideos,
      startDate: fallbackStart,
      endDate: fallbackEnd,
      modifiedAt: fallbackModifiedAt,
      defaultTeaserImage: fallbackEpisode.defaultTeaserImage,
      nextStateTimeout: timeout
    };
  }
}

/**
 * Calculates the appropriate polling interval based on the current livestream status.
 *
 * This function determines how frequently we should poll for updates based on the
 * current state of the livestream and how close we are to state transitions:
 *
 * - PRE_LIVE: Polls less frequently (30 minutes) since the MediaLivestreamStatus
 *   component handles the transition to live
 *
 * - NOW_LIVE: Polls every 5 minutes to check for updates during the livestream
 *
 * - PAST:
 *   - If within an hour of stream end: Polls every 5 minutes to quickly detect
 *     when new recordings become available
 *   - After an hour: Reduces polling frequency to every 30 minutes
 *
 * @param livestreamStatus The current status ('PRE_LIVE', 'NOW_LIVE', 'PAST', or null)
 * @param endDate The end date of the livestream (or null if not available)
 * @param currentDate The current date/time
 * @returns An object with the polling interval and optional timeout for changing the interval
 */
function getPollInterval(livestreamStatus: LiveStreamStatus['status'] | null, endDate: Date | null, currentDate: Date): {
  pollInterval: number;
  timeout: number | undefined;
} {
  if (!livestreamStatus) {
    return {
      pollInterval: 0,
      timeout: undefined
    };
  }
  switch (livestreamStatus) {
    case 'PRE_LIVE':
      // For prelive, we poll less frequently as MediaLivestreamStatus handles the transition
      return {
        pollInterval: THIRTY_MINUTES_IN_MS,
        timeout: undefined
      };
    case 'NOW_LIVE':
      // While live, we poll regularly to check for updates
      return {
        pollInterval: FIVE_MINUTES_IN_MS,
        timeout: undefined
      };
    case 'PAST':
      // If within the first hour after the stream ended, poll frequently for new recordings
      if (endDate && differenceInMinutes(currentDate, endDate) < 60) {
        return {
          pollInterval: FIVE_MINUTES_IN_MS,
          timeout: differenceInMilliseconds(addMilliseconds(endDate, ONE_HOUR_IN_MS), currentDate)
        };
      } else {
        // After an hour, we can reduce polling frequency
        return {
          pollInterval: THIRTY_MINUTES_IN_MS,
          timeout: undefined
        };
      }
    default:
      return {
        pollInterval: 0,
        timeout: undefined
      };
  }
}

/**
 * Returns the minimum of two timeout values, handling undefined cases.
 *
 * This utility function determines which timeout should trigger first
 * when managing multiple potential timeouts:
 *
 * - If both are undefined, returns undefined
 * - If one is undefined, returns the other
 * - If both are defined, returns the minimum value
 *
 * This is used to ensure we always react to the earliest required state change.
 *
 * @param timeout1 First timeout value in milliseconds, or undefined
 * @param timeout2 Second timeout value in milliseconds, or undefined
 * @returns The earlier of the two timeouts, or undefined if both are undefined
 */
function getMinTimeout(timeout1: number | undefined, timeout2: number | undefined): number | undefined {
  if (timeout1 === undefined && timeout2 === undefined) {
    return undefined;
  }
  if (timeout1 === undefined) {
    return timeout2;
  }
  if (timeout2 === undefined) {
    return timeout1;
  }
  return Math.min(timeout1, timeout2);
}

/**
 * Custom hook to manage Rundschau livestream state and polling for recordings.
 *
 * This hook orchestrates the entire lifecycle of the Rundschau livestream feature:
 *
 * 1. Data Flow:
 *    - Fetches episode data using the useRundschauUpdateQuery hook
 *    - Determines the current livestream status (PRE_LIVE, NOW_LIVE, or PAST)
 *    - Calculates appropriate polling intervals based on the status
 *    - Manages timeouts for state transitions
 *
 * 2. State Management:
 *    - Uses a counter state to trigger re-renders at calculated intervals
 *    - Manages Apollo polling for fresh data with dynamic intervals
 *    - Calculates optimal timeout values to efficiently transition between states
 *
 * 3. Return Value:
 *    - Returns the current livestream status and date for consumers to render
 *      appropriate UI components
 *    - Returns null for livestreamStatus if data is unavailable, allowing
 *      consumers to handle this case (typically by not rendering anything)
 *
 * The hook automatically adjusts its polling frequency based on the current state
 * and proximity to state transitions, optimizing for both responsiveness and
 * network efficiency.
 *
 * @param preLive The duration in milliseconds before the actual start time when the PRE_LIVE state should begin
 * @returns An object containing the current livestream status and date
 */
export function useRundschauLivestream(preLive) {
  const $ = _c(17);
  const triggerRender = useRerender();
  const currentDate = useCurrentDate();
  const {
    data,
    loading,
    startPolling,
    stopPolling
  } = useQuery(RundschauUpdateDocument);
  let livestreamStatus;
  let pollInterval;
  let t0;
  if ($[0] !== currentDate || $[1] !== data?.rundschau?.episodes?.nodes || $[2] !== preLive) {
    const [latest, fallback] = data?.rundschau?.episodes?.nodes ?? [];
    livestreamStatus = getLivestreamStatus(currentDate, latest, fallback, preLive);
    const {
      pollInterval: t1,
      timeout: pollIntervalTimeout
    } = getPollInterval(livestreamStatus?.status || null, livestreamStatus?.endDate || null, currentDate);
    pollInterval = t1;
    t0 = getMinTimeout(livestreamStatus?.nextStateTimeout, pollIntervalTimeout);
    $[0] = currentDate;
    $[1] = data?.rundschau?.episodes?.nodes;
    $[2] = preLive;
    $[3] = livestreamStatus;
    $[4] = pollInterval;
    $[5] = t0;
  } else {
    livestreamStatus = $[3];
    pollInterval = $[4];
    t0 = $[5];
  }
  const combinedTimeout = t0;
  let t1;
  if ($[6] !== triggerRender) {
    t1 = () => {
      triggerRender();
    };
    $[6] = triggerRender;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useTimeoutEffect(t1, combinedTimeout);
  let t2;
  let t3;
  if ($[8] !== pollInterval || $[9] !== startPolling || $[10] !== stopPolling) {
    t2 = () => {
      if (pollInterval > 0) {
        startPolling(pollInterval);
        return () => {
          stopPolling();
        };
      } else {
        return;
      }
    };
    t3 = [pollInterval, startPolling, stopPolling];
    $[8] = pollInterval;
    $[9] = startPolling;
    $[10] = stopPolling;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  let t4;
  if ($[13] !== currentDate || $[14] !== livestreamStatus || $[15] !== loading) {
    t4 = {
      livestreamStatus,
      currentDate,
      loading
    };
    $[13] = currentDate;
    $[14] = livestreamStatus;
    $[15] = loading;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}